import React, { useState } from 'react';
// Api
import client from '../../api/client';
// Data
import { emptyContactFormData } from '../../utils/data/FormData';
// Utils
import LoadingSpinner from '../utils/LoadingSpinner';
// Constants
import { SUBMIT_CONTACT_FORM_API } from '../../utils/ApiRoutes';

function ContactForm() {
  const submitStates = ['waiting', 'inprogress', 'failed', 'success'];
  const [formData, setFormData] = useState(emptyContactFormData);
  const [submitState, setSubmitState] = useState(submitStates[0]);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setSubmitState(submitStates[0]);
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.message) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitState(submitStates[1]);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      setSubmitState(submitStates[0]);
      return;
    }

    client
      .post(SUBMIT_CONTACT_FORM_API, formData, false)
      .then(() => {
        setSubmitState(submitStates[3]);
        setFormData(emptyContactFormData);
        setErrors({});
      })
      .catch((err) => {
        console.error('Unable to submit contact request', err);
        setSubmitState(submitStates[2]);
      });
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-y-5 md:gap-y-6">
      {/* Row 1 */}
      <section className="grid lg:grid-cols-2 gap-3">
        <div className="grid gap-1">
          <label className="text-colour7 text-sm font-bold" htmlFor="firstName">
            First Name <span className="text-error-red">*</span>
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            autoComplete="on"
            className={`w-full rounded bg-colour6 border border-colour7 border-solid text-colour1 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-colour6 focus:border-colour6 ${
              errors.firstName ? 'border-error-red' : ''
            }`}
            required
            aria-required="true"
            aria-invalid={errors.firstName ? 'true' : 'false'}
            aria-describedby="firstNameError"
          />
          {errors.firstName && (
            <p id="firstNameError" className="text-error-red text-xs italic">{errors.firstName}</p>
          )}
        </div>

        <div className="grid gap-1">
          <label className="text-colour7 text-sm font-bold" htmlFor="lastName">
            Last Name <span className="text-error-red">*</span>
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            autoComplete="on"
            className={`w-full rounded bg-colour6 border border-colour7 border-solid text-colour1 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-colour6 focus:border-colour6 ${
              errors.lastName ? 'border-error-red' : ''
            }`}
            required
            aria-required="true"
            aria-invalid={errors.lastName ? 'true' : 'false'}
            aria-describedby="lastNameError"
          />
          {errors.lastName && (
            <p id="lastNameError" className="text-error-red text-xs italic">{errors.lastName}</p>
          )}
        </div>
      </section>

      {/* Row 2 */}
      <section className="grid gap-1">
        <label className="text-colour7 text-sm font-bold" htmlFor="email">
          Email <span className="text-error-red">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          autoComplete="on"
          className={`w-full rounded bg-colour6 border border-colour7 border-solid text-colour1 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-colour6 focus:border-colour6 ${
            errors.email ? 'border-error-red' : ''
          }`}
          required
          aria-required="true"
          aria-invalid={errors.email ? 'true' : 'false'}
          aria-describedby="emailError"
        />
        {errors.email && (
          <p id="emailError" className="text-error-red text-xs italic">{errors.email}</p>
        )}
      </section>

      <section className="grid lg:grid-cols-2 gap-3">
        <div className="grid gap-1">
          <label className="text-colour7 text-sm font-bold" htmlFor="phoneNumber">
            Phone Number (optional)
          </label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            className="w-full rounded bg-colour6 border border-colour7 border-solid text-colour1 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-colour6 focus:border-colour6"
          />
        </div>

        <div className="grid gap-1">
          <label className="text-colour7 text-sm font-bold" htmlFor="location">
            Location (optional)
          </label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            className="w-full rounded bg-colour6 border border-colour7 border-solid text-colour1 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-colour6 focus:border-colour6"
          />
        </div>
      </section>

      {/* Message */}
      <div className="grid gap-1">
        <label className="text-colour7 text-sm font-bold" htmlFor="message">
          Message <span className="text-error-red">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          rows={6}
          onChange={handleChange}
          className={`w-full rounded bg-colour6 border border-colour7 border-solid text-colour1 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-colour6 focus:border-colour6 ${
            errors.message ? 'border-error-red' : ''
          }`}
          required
          aria-required="true"
          aria-invalid={errors.message ? 'true' : 'false'}
          aria-describedby="messageError"
        />
        {errors.message && (
          <p id="messageError" className="text-error-red text-xs italic">{errors.message}</p>
        )}
      </div>

      {/* Submit */}
      <div className="grid">
        <button
          type="submit"
          className="grid place-items-center w-full bg-colour6 hover:bg-colour7 active:bg-colour6/80 text-white py-3 rounded font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-colour7 neon_text_thin_magenta"
          aria-label="Submit your form"
          disabled={submitState === submitStates[1]}
        >
          {submitState === submitStates[1] ? (
            <div className="grid place-items-center">
              <LoadingSpinner xs={true} />
            </div>
          ) : (
            <span>Submit</span>
          )}
        </button>
      </div>

      {/* Alerts */}
      {submitState === submitStates[2] && (
        <div role="alert" aria-live="assertive" className="text-error-red mt-2 text-center">
          Unable to submit the form. Please try again.
        </div>
      )}
      {submitState === submitStates[3] && (
        <div role="alert" aria-live="polite" className="text-green-500 mt-2 text-center">
          Form submitted successfully!
        </div>
      )}
    </form>
  );
}

export default ContactForm;
